import React, { useEffect, useRef, useState } from "react";

import { getResiduePayoutPotential } from "services";

import { scrollIntoViewWithObserver } from "utils";

import { CROP_CREATION_MODAL_TYPE, CROP_TYPE, CropExplorationEntry, CropExplorationResponse, CropExplorationTemplateResponse } from "models";

import { Popover } from "components";

import { CropCardWrapper } from "pages/app/crop-explorer/components/CropCardWrapper";
import { CropCreationForm } from "pages/app/crop-explorer/components/CropCreationForm";

export type CropCreationModalProps = {
  cardId: string;
  type: CROP_CREATION_MODAL_TYPE;
  cropType: CROP_TYPE;
  cropExploration: CropExplorationResponse | CropExplorationTemplateResponse;
  year: number;
  crop?: CropExplorationEntry | null;
  isTourStep?: boolean;
  isAdmin?: boolean;
};

export const CropCreationPopover = ({ cardId, type, crop, cropType, cropExploration, isTourStep, year, isAdmin }: CropCreationModalProps) => {
  const cropCardWrapperRef = useRef<HTMLDivElement | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [isAddingCrop, setIsAddingCrop] = useState<boolean>(false);
  const [cropResiduePayout, setCropResiduePayout] = useState<number>(0);

  // We calculate the residue payout before rendering the CropCreationForm component to have a value we can use to initialize the modal, this way
  // the component will not change size after the first render and the modal won't need to vertically adjust its position
  useEffect(() => {
    if (!crop || !cropExploration || isAdmin) {
      return;
    }

    getResiduePayoutPotential({
      cropId: crop.cropId,
      hectares: (cropExploration as CropExplorationResponse).hectares,
      regionId: (cropExploration as CropExplorationResponse).regionId,
    }).then((res) => {
      setCropResiduePayout(res.residuePayoutAvg as number);
    });
  }, [cropExploration, crop, isAdmin]);

  // When adding a new crop, keep the state as "active" until the new crop has been fetched to avoid card flickering between active and empty state.
  useEffect(() => {
    if (!isAddingCrop) return;
    if (crop) setIsAddingCrop(false);
  }, [crop, cropExploration, isAddingCrop]);

  return (
    <Popover
      className="crop-explorer"
      content={
        <CropCreationForm
          cardId={cardId}
          type={type}
          cropType={cropType}
          crop={(crop && { cropId: crop.cropId, id: crop.id, hasResidue: crop.residue }) || undefined}
          cropExploration={cropExploration}
          year={year}
          cropResiduePayout={cropResiduePayout}
          afterAddOrUpdate={() => {
            setShowModal(false);
            setIsAddingCrop(true);
          }}
          onClose={() => {
            setShowModal(false);
          }}
          isAdmin={isAdmin}
        />
      }
      dataTestId={`crop-explorer-${type}-crop-modal`}
      trigger="click"
      placement="left"
      open={showModal}
      onOpenChange={async () => {
        await scrollIntoViewWithObserver(cropCardWrapperRef);
        setShowModal(!showModal);
      }}
      destroyOnHidden={true}
    >
      <div onClick={() => setShowModal(!showModal)} ref={cropCardWrapperRef}>
        <CropCardWrapper
          dataTestId={`${year}-${cropType.toLowerCase()}-${crop?.id || "empty"}`}
          hasCrop={!!crop}
          cropType={cropType}
          isActive={showModal || isAddingCrop}
          cropCardProps={crop && { cropId: crop.cropId, cropName: crop.name ?? "Sample name", hasResidue: crop.residue, isActive: showModal }}
          isTourStep={isTourStep}
        />
      </div>
    </Popover>
  );
};
